import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, UserRole, hasRole } from '../../stores/authStore';
import { useCompanyStore } from '../../stores/companyStore';
import {
  isAdminWorkspacePath,
  isAllowedEmail,
  isFounderAdminEmail,
  shouldUseTenantWorkspace,
} from '../../lib/founder-access';

const SECTOR_HOME: Record<string, string> = {
  mining: '/sector/mining',
  logistics: '/sector/fleet',
  agriculture: '/sector/farm',
  construction: '/sector/jobsite',
};

const OPERATOR_HOME = '/operator/mining/shift';
const DRIVER_WEB_ALLOWLIST = new Set<string>(['/mobile-only', '/profile']);

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: UserRole[];
  allowedEmails?: string[];
}

export default function ProtectedRoute({ children, roles, allowedEmails }: ProtectedRouteProps) {
  const { isAuthenticated, user, loadFromStorage } = useAuthStore();
  const { sector, loadCompany } = useCompanyStore();
  const location = useLocation();

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  // Load company sector if authenticated but sector not yet loaded
  useEffect(() => {
    if (isAuthenticated && !sector && shouldUseTenantWorkspace(user)) loadCompany();
  }, [isAuthenticated, sector, loadCompany, user]);

  const hasToken = !!localStorage.getItem('token');
  const userIsFounderAdmin = isFounderAdminEmail(user?.email);

  // 1. Not authenticated at all → login
  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // A token without a hydrated user is an indeterminate auth state. Rendering
  // children here briefly exposes role-gated shells before the store settles.
  if (hasToken && !user) {
    return null;
  }

  // Founder/admin accounts are not tenant operators. Keep them inside the
  // business workspace so they never inherit sector dashboards through role or
  // tenant metadata.
  if (userIsFounderAdmin && !isAdminWorkspacePath(location.pathname)) {
    return <Navigate to="/admin" replace />;
  }

  // 2. Owner who needs onboarding
  const isOnboardingRoute = location.pathname === '/onboarding';
  const needsOnboarding = !userIsFounderAdmin && user?.needsOnboarding && hasRole(user, UserRole.OWNER);
  if (needsOnboarding && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace />;
  }

  // 3. Sector users trying to hit /dashboard → redirect to their real home
  const isSectorUser = sector && SECTOR_HOME[sector];
  if (!userIsFounderAdmin && isSectorUser && location.pathname === '/dashboard') {
    return <Navigate to={SECTOR_HOME[sector]} replace />;
  }

  // 4. Operators now have a dedicated web shell. Drivers remain mobile-only.
  if (!userIsFounderAdmin && user?.role === UserRole.OPERATOR) {
    const operatorAllowed =
      location.pathname.startsWith('/operator/')
      || location.pathname === '/profile';

    if (!operatorAllowed) {
      return <Navigate to={OPERATOR_HOME} replace />;
    }
  }

  if (!userIsFounderAdmin && user?.role === UserRole.DRIVER && !DRIVER_WEB_ALLOWLIST.has(location.pathname)) {
    return <Navigate to="/mobile-only" replace />;
  }

  // 5. Role-restricted route
  if (!userIsFounderAdmin && roles && user && !hasRole(user, ...roles)) {
    return <Navigate to="/dashboard" replace />;
  }

  // 6. Email-restricted route (founder/admin pages)
  if (allowedEmails && user && !isAllowedEmail(user.email, allowedEmails)) {
    return <Navigate to={userIsFounderAdmin ? '/admin' : '/dashboard'} replace />;
  }

  return <>{children}</>;
}
